import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { drawDocumentHeader } from "./documentHeader";

const CAUSALE_LABEL = {
  consegna: "Consegna",
  ritiro: "Ritiro",
  reso: "Reso",
};

// Formatta un numero in stile italiano: virgola decimale, sempre 2 decimali.
const fmtNum = (n) =>
  Number(n).toLocaleString("it-IT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// Etichetta scatole con singolare/plurale (interi).
const boxesLabel = (n) => {
  const r = Math.round(Number(n) || 0);
  return `${r} ${r === 1 ? "scatola" : "scatole"}`;
};

/**
 * Quantità di riga per il PDF DDT.
 * - AREA (salesUnit M2): mq = quantità(scatole) × sqmPerBox → "X,XX mq (N scatole)"
 * - LINEARE (salesUnit ML): ml = quantità(scatole) × mlPerBox → "X,XX ml (N scatole)"
 * - PEZZI (salesUnit PZ/UD): "X pz"
 * La quantità è quella effettivamente consegnata nel singolo DDT (ddt_items.quantity),
 * quindi corretta anche per le consegne parziali. Fallback sicuro (nessun NaN) quando
 * il fattore per scatola non è disponibile.
 */
function formatDdtQuantity(it) {
  const qty = Number(it.quantity);
  if (!Number.isFinite(qty)) return "—";
  const unit = (it.item?.salesUnit || it.uom || "").toUpperCase();

  if (unit === "M2" || unit === "MQ") {
    const sqm = Number(it.item?.sqmPerBox);
    if (Number.isFinite(sqm) && sqm > 0) {
      return `${fmtNum(qty * sqm)} mq (${boxesLabel(qty)})`;
    }
    return boxesLabel(qty);
  }
  if (unit === "ML") {
    const ml = Number(it.item?.mlPerBox);
    if (Number.isFinite(ml) && ml > 0) {
      return `${fmtNum(qty * ml)} ml (${boxesLabel(qty)})`;
    }
    return boxesLabel(qty);
  }
  if (unit === "PZ" || unit === "UD") {
    return `${Math.round(qty)} pz`;
  }
  // Altre unità (CONF, SC, KG, PAL, …): mostra la quantità con la sua UdM.
  return `${Math.round(qty)} ${(it.uom || "pz").toLowerCase()}`;
}

/**
 * Genera un PDF DDT e lo restituisce come Blob.
 * @param {Object} ddt - { id, causale, date, client?, magazziniere?, operatore?, ddt_items }
 * @returns {Blob}
 */
export function generateDdtPdf(ddt) {
  const doc = new jsPDF({ unit: "mm", format: "a4", compress: true });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 10;
  let y = 10;

  // ---- HEADER ----
  y = drawDocumentHeader(doc, { pageWidth, marginX, y });

  // ---- TITOLO DDT ----
  doc.setFontSize(16).setFont("helvetica", "bold");
  doc.text("DOCUMENTO DI TRASPORTO", pageWidth / 2, y, { align: "center" });
  y += 7;
  doc.setFontSize(11).setFont("helvetica", "normal");
  const ddtDate = ddt.date
    ? new Date(ddt.date).toLocaleDateString("it-IT", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : new Date().toLocaleDateString("it-IT", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
  const causaleText =
    ddt.causale === "consegna"
      ? `Consegna ${ddt.sequence ?? 1}`
      : CAUSALE_LABEL[ddt.causale] || ddt.causale;
  doc.text(
    `DDT N° ${ddt.id}  –  Data: ${ddtDate}  –  Causale: ${causaleText}`,
    pageWidth / 2,
    y,
    { align: "center" }
  );
  y += 8;

  // ---- LINEA SEPARATRICE ----
  doc.setDrawColor(150).line(marginX, y, pageWidth - marginX, y);
  y += 6;

  // ---- DESTINATARIO + N° PREVENTIVO ----
  doc.setFontSize(10);
  if (ddt.client?.clientName) {
    doc.setFont("helvetica", "bolditalic");
    doc.text("Committente:", marginX, y);
    const destOff = doc.getTextWidth("Committente: ") + 0.8;
    doc.setFont("helvetica", "normal");
    doc.text(ddt.client.clientName, marginX + destOff, y);
  }
  if (ddt.quoteId) {
    doc.setFont("helvetica", "bolditalic");
    const prevLabel = `Preventivo N° ${ddt.quoteId}`;
    doc.text(prevLabel, pageWidth - marginX - doc.getTextWidth(prevLabel), y);
  }
  if (ddt.client?.clientName || ddt.quoteId) y += 6;

  // ---- INDIRIZZO DI CONSEGNA / MODALITÀ ----
  doc.setFontSize(10);
  if (ddt.causale === "consegna") {
    const rawAddr = ddt.quote?.location?.address;
    const address =
      typeof rawAddr === "string" && rawAddr.trim()
        ? rawAddr.trim()
        : "Non specificato";
    doc.setFont("helvetica", "bolditalic");
    doc.text("Indirizzo di consegna:", marginX, y);
    const addrOff = doc.getTextWidth("Indirizzo di consegna: ") + 0.8;
    doc.setFont("helvetica", "normal");
    const addrLines = doc.splitTextToSize(
      address,
      pageWidth - 2 * marginX - addrOff
    );
    doc.text(addrLines, marginX + addrOff, y);
    y += addrLines.length * 5 + 2;
  } else {
    doc.setFont("helvetica", "bolditalic");
    doc.text("Modalità:", marginX, y);
    const modOff = doc.getTextWidth("Modalità: ") + 0.8;
    doc.setFont("helvetica", "normal");
    doc.text(CAUSALE_LABEL[ddt.causale] || ddt.causale, marginX + modOff, y);
    y += 6;
  }

  // ---- TABELLA ARTICOLI ----
  const items = ddt.ddt_items || [];
  const body = items.map((it) => [
    it.description || "—",
    formatDdtQuantity(it),
  ]);

  autoTable(doc, {
    startY: y,
    theme: "grid",
    head: [["Descrizione", "Quantità"]],
    body,
    margin: { left: marginX, right: marginX },
    styles: { fontSize: 10, cellPadding: 1.5 },
    headStyles: { fillColor: [200, 200, 200], fontStyle: "bold" },
    columnStyles: {
      0: { cellWidth: "auto" },
      1: { cellWidth: 60, halign: "right" },
    },
  });

  y = doc.lastAutoTable.finalY + 8;

  // ---- NOTE (solo se valorizzate) ----
  if (ddt.notes && String(ddt.notes).trim()) {
    const noteLines = doc.splitTextToSize(
      String(ddt.notes).trim(),
      pageWidth - 2 * marginX
    );
    if (y + 6 + noteLines.length * 4.5 + 4 > pageHeight - 15) {
      doc.addPage();
      y = 15;
    }
    doc.setFont("helvetica", "bold").setFontSize(10);
    doc.text("Note:", marginX, y);
    y += 5;
    doc.setFont("helvetica", "normal").setFontSize(9);
    for (const line of noteLines) {
      if (y + 4.5 > pageHeight - 12) {
        doc.addPage();
        y = 15;
      }
      doc.text(line, marginX, y);
      y += 4.5;
    }
    y += 5;
  }

  // ---- SPAZIO FIRME (gray box, due colonne) ----
  if (y + 50 > pageHeight - 20) {
    doc.addPage();
    y = 20;
  }

  const colPadX = 8;
  const headPadTop = 10;
  const sigLineYOffset = 12;
  const lineH = 6;
  const sigWidth = 60;
  const blockH = Math.max(40, headPadTop + 7 + lineH + sigLineYOffset + 8);

  doc.setFillColor(240, 240, 240);
  doc.rect(marginX, y, pageWidth - 2 * marginX, blockH, "F");

  const col1X = marginX + colPadX;
  const col2X = pageWidth / 2 + colPadX;

  const magNome = ddt.magazziniere?.operatorName || "—";
  const clienteNome = ddt.client?.clientName || "—";

  const renderSigCol = (x, title, name) => {
    let fy = y + headPadTop;
    doc.setFont("helvetica", "bold").setFontSize(12);
    doc.text(title, x, fy);
    fy += 7;
    doc.setFont("helvetica", "normal").setFontSize(11);
    doc.text(name, x, fy);
    fy += lineH;
    doc.setFont("helvetica", "bold").setFontSize(12);
    doc.text("Firma", x, fy);
    fy += sigLineYOffset;
    doc.setDrawColor(0);
    doc.line(x, fy, x + sigWidth, fy);
  };

  renderSigCol(col1X, "Magazziniere", magNome);
  renderSigCol(col2X, "Committente", clienteNome);

  return doc.output("blob");
}
